import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { useTvFreshness, tvLoadErrorMessage } from '../hooks/useTvFreshness';
import { useOnReconnect } from '../hooks/useOnReconnect';
import { formatSavedAt } from '../services/connectionStatus';
import { readTvSnapshot, writeTvSnapshot } from '../utils/tvSnapshot';
import { getTeamMeta, getDisplayName, formatScoreAbbreviated, formatPercent, TeamIcon, compareByStasisThenScore, countInContention } from '../utils/transformersCompetition';
import RoundCountdown from '../components/RoundCountdown';
import { BatteryIcon } from '../components/BatteryMeter';
import './TransformersTvDashboard.css';

const REFRESH_INTERVAL_MS = 45000;
const AUTO_SCROLL_SPEED_PX = 0.4;
const AUTO_SCROLL_TICK_MS = 30;
const AUTO_SCROLL_PAUSE_MS = 2500;

// Same points-based surplus used on the Broadcast page - how far above target a player's best
// score is, floored at 0 (missing target contributes nothing).
function memberPointsSurplus(member) {
  return member.score > member.targetWithRust ? member.score - member.targetWithRust : 0;
}

// Slowly, continuously scrolls a roster div up and down on a loop (pausing briefly at each end)
// so every player eventually appears on screen - this is a passive kiosk display nobody stands
// at scrolling manually, so with 20+ players on a team the bottom of the list would otherwise
// never be seen. A no-op if the content already fits (nothing to scroll).
function useAutoScroll() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let direction = 1;
    let pauseUntil = 0;

    const interval = setInterval(() => {
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) return;

      const now = Date.now();
      if (now < pauseUntil) return;

      el.scrollTop += AUTO_SCROLL_SPEED_PX * direction;

      if (el.scrollTop >= maxScroll) {
        el.scrollTop = maxScroll;
        direction = -1;
        pauseUntil = now + AUTO_SCROLL_PAUSE_MS;
      } else if (el.scrollTop <= 0) {
        el.scrollTop = 0;
        direction = 1;
        pauseUntil = now + AUTO_SCROLL_PAUSE_MS;
      }
    }, AUTO_SCROLL_TICK_MS);

    return () => clearInterval(interval);
    // Empty deps: start the loop once per mount. maxScroll/scrollTop are re-read from the DOM
    // every tick, so it naturally adapts as the roster's content changes on data refresh -
    // re-running this effect on every render (e.g. the header's once-a-second clock tick) would
    // just tear down and restart the interval constantly, wiping out any in-progress pause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
}

function TeamColumn({ team, isLeader }) {
  const meta = getTeamMeta(team.id);
  const rosterRef = useAutoScroll();
  // Players who never put in a single score, all competition long, are hidden from the roster
  // list here - they're still on the team and tracked, just not worth screen space on a display
  // nobody scrolls. The header counts below are independent of that and run over the full
  // roster: total size, and how many are still in contention.
  const playedMembers = team.members.filter(m => m.hasEverPlayed);
  const inContentionCount = countInContention(team.members);
  const sortedMembers = [...playedMembers].sort(compareByStasisThenScore);
  // Index of the first Stasis Locked player in the sorted list, i.e. where the in-contention
  // group ends. -1 when the whole team is one or the other, in which case no divider is drawn.
  const firstLockedIndex = sortedMembers.findIndex(m => m.isStasisLocked);
  const lockedDividerIndex = firstLockedIndex > 0 ? firstLockedIndex : -1;

  return (
    <div className={`tv-team-column team-${team.id} ${isLeader ? 'is-leader' : ''}`}>
      <div className="tv-team-column-header">
        <div className="tv-team-name"><TeamIcon teamId={team.id} size="1.6vw" style={{ marginRight: '0.4vw' }} /> {meta.name}</div>
        <div className="tv-team-total-row">
          <div className="tv-team-total">+{formatScoreAbbreviated(team.pointsTotal)}</div>
          {isLeader && <span className="tv-leader-badge">&#128081; Leader</span>}
        </div>
        <div className="tv-team-meta">
          {team.members.length} members
          {' - '}<span className="tv-team-in-contention">{inContentionCount} in contention</span>
        </div>
      </div>
      <div className="tv-team-column-roster" ref={rosterRef}>
        {sortedMembers.length === 0 && <div className="tv-danger-empty">No scores yet.</div>}
        {sortedMembers.map((member, index) => {
          const points = memberPointsSurplus(member);
          const key = member.userId || member.sternUsername;
          return (
            <React.Fragment key={key}>
              {index === lockedDividerIndex && (
                <div className="tv-roster-divider">Stasis Locked</div>
              )}
              <div className={`tv-player-row ${member.isStasisLocked ? 'is-locked' : ''}`}>
                <div className="tv-player-name">
                  <span className="tv-player-name-text">{getDisplayName(member)}</span>
                  {member.rustPoints > 0 && <span className="tv-rust-badge">Rust {member.rustPoints}</span>}
                </div>
                <div className="tv-player-score">{member.score > 0 ? formatScoreAbbreviated(member.score) : ' - '}</div>
                <div className={`tv-player-surplus ${points > 0 ? 'is-positive' : ''}`}>
                  {points > 0 ? `+${formatScoreAbbreviated(points)}` : ''}
                </div>
                <div className="tv-player-battery">
                  <BatteryIcon size="0.9vw" pct={member.energonBattery} isStasisLocked={member.isStasisLocked} />
                  {member.isStasisLocked ? 'LOCK' : formatPercent(member.energonBattery)}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function TransformersTvDashboard() {
  const { id } = useParams();
  // The last live dashboard this browser saw, so a reload with the server
  // down still shows the standings, marked as not live (utils/tvSnapshot.js).
  const snapshotKey = `competition-tv-${id}`;
  const [snapshot] = useState(() => readTvSnapshot(snapshotKey));
  const [data, setData] = useState(() => snapshot?.data ?? null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(new Date());
  const intervalRef = useRef(null);
  const { stale, asOf, noteResponses } = useTvFreshness(snapshot?.savedAt ?? null);

  const load = async () => {
    try {
      const res = await apiService.getCompetitionTvDashboard(id);
      setData(res.data);
      if (noteResponses([res])) writeTvSnapshot(snapshotKey, res.data);
      setError('');
    } catch (err) {
      // Shown only while there is nothing on screen. With data up, the
      // freshness pill says the standings are not live instead.
      setError(tvLoadErrorMessage(err, 'Unable to load dashboard'));
    }
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, REFRESH_INTERVAL_MS);
    const clockInterval = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(clockInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The server is back: reload now rather than at the next 45-second refresh.
  useOnReconnect(load);

  if (error && !data) {
    return <div className="tv-dashboard tv-dashboard-error">{error}</div>;
  }

  if (!data) {
    return <div className="tv-dashboard tv-dashboard-loading">Loading Transformers Pinball Championship...</div>;
  }

  const { competition, round, teams, grandChampion } = data;

  if (competition.status === 'completed' && grandChampion) {
    return (
      <div className="tv-dashboard tv-champion-screen">
        <div className="tv-champion-crown">&#128081;</div>
        <div className="tv-champion-label">Grand Champion</div>
        <div className="tv-champion-name">{getDisplayName(grandChampion)}</div>
        <div className="tv-champion-sub">{competition.name}</div>
      </div>
    );
  }

  const teamsWithTotals = teams.map(team => ({
    ...team,
    pointsTotal: team.members.reduce((sum, m) => sum + memberPointsSurplus(m), 0)
  }));
  const rankedTeams = [...teamsWithTotals].sort((a, b) => (b.energonTotal || 0) - (a.energonTotal || 0));

  return (
    <div className="tv-dashboard">
      <header className="tv-header">
        <div className="tv-header-title">{competition.name}</div>
        <div className="tv-header-right">
          <span className={`tv-status-pill ${round?.status === 'active' ? 'active' : 'closed'}`}>
            {round ? `Week ${round.round_number} - ${round.status === 'active' ? 'In Progress' : 'Just Closed'}` : 'No active week'}
          </span>
          <span className="tv-clock">{now.toLocaleTimeString()}</span>
        </div>
      </header>

      <section className="tv-team-columns">
        {rankedTeams.map((team, index) => (
          <TeamColumn key={team.id} team={team} isLeader={index === 0} />
        ))}
      </section>

      <footer className="tv-footer">
        {/* In the footer rather than the header: the title and week pill already
            fill the header, and a third item made everything in it wrap. */}
        {stale && asOf && (
          <span className="tv-offline">Offline · standings as of {formatSavedAt(asOf)}</span>
        )}
        {round?.status === 'active' && <RoundCountdown className="tv-countdown" />}
      </footer>
    </div>
  );
}

export default TransformersTvDashboard;
